"use client";

import { Fragment } from "react";
import { ArrowRight, Check } from "lucide-react";
import {
  ServicePageLayout,
  ConsultationCTA,
  ServiceHeroImage,
  ServiceSection,
  ServiceGrid,
  ServiceFeatureCard,
  ServiceFaq,
} from "@/components/services";
import { StructuredData } from "@/components/common";
import { useLocale } from "@/content/i18n/context";
import { t } from "@/content/i18n/messages";

const dtcImage = "/images/services/svc-digital-transformation.jpg";

// Cancels the layout container padding so band sections read full-width.
const bandBleed = "-mx-4 sm:-mx-6 lg:-mx-8";

const transformationStages = [
  {
    number: "01",
    title: "Stage 1: Digitization",
    description:
      "The foundational stage of converting analog information into digital formats, making data accessible and searchable:",
    features: [
      "Document digitization and management systems",
      "Digital record-keeping and archiving",
      "Paper-to-digital workflow conversion",
      "Digital data collection and storage solutions",
    ],
  },
  {
    number: "02",
    title: "Stage 2: Digitalization",
    description:
      "Leveraging digital technologies to improve business processes and workflows:",
    features: [
      "Business process automation",
      "Digital workflow implementation",
      "Cloud-based collaboration tools",
      "Digital customer service platforms",
      "Enterprise resource planning (ERP) systems",
    ],
  },
  {
    number: "03",
    title: "Stage 3: Digital Transformation",
    description: "Complete organizational change through digital technologies:",
    features: [
      "Data-driven decision making",
      "AI and machine learning integration",
      "Digital business models and revenue streams",
      "Enhanced customer experience through digital channels",
      "Digital culture and workforce transformation",
    ],
  },
];

const methodologySteps = [
  {
    title: "Assessment & Strategy",
    description:
      "Comprehensive digital maturity assessment and roadmap development.",
  },
  {
    title: "Technology Implementation",
    description: "Strategic deployment of digital solutions and platforms.",
  },
  {
    title: "Change Management",
    description:
      "Comprehensive training and adoption support for smooth transition.",
  },
  {
    title: "Continuous Optimization",
    description: "Ongoing monitoring and improvement of digital initiatives.",
  },
];

const dtcFAQs = [
  {
    question: "What is digital transformation and why does my business need it?",
    answer: "Digital transformation is the process of integrating digital technology into all areas of your business, fundamentally changing how you operate and deliver value to customers. Malaysian businesses need it to stay competitive, improve efficiency, reduce costs, and meet evolving customer expectations in an increasingly digital economy."
  },
  {
    question: "How long does a digital transformation project take in Malaysia?",
    answer: "A typical digital transformation project takes 3 to 12 months depending on scope. Small to medium digitalization projects (Stage 2) usually take 3-6 months, while full enterprise-wide transformation (Stage 3) can take 6-12 months. Anchor Sprint uses an agile approach to deliver value incrementally."
  },
  {
    question: "How much does digital transformation cost for Malaysian SMEs?",
    answer: "Costs vary widely based on scope. A focused digitalization project can start from RM 30,000-80,000, while comprehensive enterprise transformation ranges from RM 150,000 to RM 500,000+. Anchor Sprint offers phased approaches so SMEs can start small and scale. Malaysian businesses may also qualify for MDEC grants and incentives."
  },
  {
    question: "What industries benefit most from digital transformation in Malaysia?",
    answer: "Manufacturing, retail, financial services, healthcare, and logistics see the highest ROI from digital transformation in Malaysia. However, any business that handles data, serves customers, or manages supply chains can benefit significantly from digitalization and process automation."
  },
  {
    question: "Does Anchor Sprint provide digital transformation consulting only, or full implementation?",
    answer: "Anchor Sprint provides end-to-end services: from strategy and consulting through to full implementation, training, and ongoing support. Our team handles assessment, technology selection, development, deployment, change management, and continuous optimization — all under one roof."
  }
];

export default function DigitalTransformation() {
  const locale = useLocale();
  const translate = (text: string) => t(locale, text);
  const translatedFaqs = dtcFAQs.map((faq) => ({
    question: translate(faq.question),
    answer: translate(faq.answer),
  }));

  return (
    <ServicePageLayout
      title="Digital Transformation and Consulting in Malaysia"
      breadcrumb="Services / Digital Transformation"
      currentServiceId="dtc"
      accent="Digital Transformation"
      dek="Strategy through implementation — we guide Malaysian organizations from digitization to full digital transformation."
    >
      <ServiceHeroImage
        src={dtcImage}
        alt="Digital Transformation Services Malaysia"
      />

      <div className="mb-16">
        <h2 className="mb-6 font-display text-3xl font-bold text-neutral-emphasis">
          {translate("Complete Digital Transformation Services in Malaysia")}
        </h2>
        <p className="max-w-2xl leading-relaxed text-neutral-muted">
          {translate(
            "At Anchor Sprint, we guide Malaysian businesses through comprehensive digital transformation, helping organizations evolve through three strategic stages: Digitization, Digitalization, and Digital Transformation. Our approach ensures sustainable digital growth and competitive advantage in the modern business landscape.",
          )}
        </p>
      </div>

      <ServiceSection
        title="The Three Stages of Digital Transformation"
        titleLevel="h2"
        eyebrow="The journey"
        accent="Three Stages"
        dek="Every organization is somewhere on this path. We meet you where you are and engineer the next stage."
        band="ivory"
        className={bandBleed}
      >
        {/* Dark proof moment — the transformation path as a horizontal progression */}
        <div className="rounded-3xl bg-hero-background px-6 py-10 sm:px-10 lg:px-12 lg:py-14">
          <div className="grid gap-10 lg:grid-cols-[1fr_auto_1fr_auto_1fr] lg:gap-6">
            {transformationStages.map((stage, index) => (
              <Fragment key={stage.title}>
                {index > 0 && (
                  <div className="hidden items-center justify-center lg:flex">
                    <ArrowRight
                      className="h-6 w-6 text-primary-contrast/40"
                      aria-hidden
                    />
                  </div>
                )}
                <div className="border-t border-primary-contrast/15 pt-6">
                  <span
                    className="font-display text-5xl font-bold text-primary-contrast/30"
                    aria-hidden
                  >
                    {stage.number}
                  </span>
                  <h3 className="mt-4 font-display text-xl font-semibold text-primary-contrast">
                    {translate(stage.title)}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-primary-contrast/70">
                    {translate(stage.description)}
                  </p>
                  <ul className="mt-5 space-y-2.5">
                    {stage.features.map((feature) => (
                      <li
                        key={feature}
                        className="flex items-start gap-2.5 text-sm text-primary-contrast/60"
                      >
                        <Check
                          className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary-contrast/40"
                          aria-hidden
                        />
                        <span>{translate(feature)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Fragment>
            ))}
          </div>
        </div>
      </ServiceSection>

      <ServiceSection
        title="Benefits of Digital Transformation"
        titleLevel="h2"
        eyebrow="Why it pays"
        accent="Benefits"
        dek="What changes for your business when digital stops being a project and becomes the operating model."
        band="surface"
        className={bandBleed}
      >
        <ServiceGrid columns={2}>
          <ServiceFeatureCard
            icon="fas fa-chart-line"
            title="Operational Excellence"
            description="Achieve greater efficiency, reduced costs, and improved productivity through streamlined digital processes."
          />

          <ServiceFeatureCard
            icon="fas fa-users"
            title="Enhanced Customer Experience"
            description="Deliver personalized, seamless interactions across all digital touchpoints."
          />

          <ServiceFeatureCard
            icon="fas fa-lightbulb"
            title="Innovation Capability"
            description="Stay ahead of market trends with rapid innovation and digital capabilities."
          />

          <ServiceFeatureCard
            icon="fas fa-shield-alt"
            title="Competitive Advantage"
            description="Gain market leadership through digital differentiation and agility."
          />
        </ServiceGrid>
      </ServiceSection>

      <ServiceSection
        title="Our Digital Transformation Methodology"
        titleLevel="h2"
        eyebrow="How we work"
        accent="Methodology"
        dek="Four steps, repeated in agile cycles until transformation sticks."
        band="ivory"
        className={bandBleed}
      >
        <ol className="divide-y divide-border border-y border-border">
          {methodologySteps.map((step, index) => (
            <li
              key={step.title}
              className="grid gap-3 py-8 sm:grid-cols-[auto_1fr] sm:gap-8"
            >
              <span className="font-mono text-sm text-primary" aria-hidden>
                0{index + 1}
              </span>
              <div>
                <h3 className="font-display text-xl font-semibold text-neutral-emphasis">
                  {translate(step.title)}
                </h3>
                <p className="mt-2 max-w-prose leading-relaxed text-neutral-muted">
                  {translate(step.description)}
                </p>
              </div>
            </li>
          ))}
        </ol>
      </ServiceSection>

      {/* FAQ Section for rich snippets */}
      <ServiceSection
        title="Frequently Asked Questions"
        titleLevel="h2"
        eyebrow="FAQ"
        band="surface"
        className={bandBleed}
      >
        <ServiceFaq items={dtcFAQs} />
      </ServiceSection>
      <StructuredData type="faq" faqs={translatedFaqs} />

      <ConsultationCTA
        title="Start Your Digital Transformation Journey Today"
        description="Let's discuss how we can help your organization embrace digital innovation and achieve sustainable growth in the Malaysian market."
      />
    </ServicePageLayout>
  );
}
